/**
 * PII-invoercontrole (issue #31), twee trappen:
 *
 * Trap 1 — `detectStructuredPii`: STRUCTURED identifiers where a regex is
 * near-certain: BSN (with elfproef), Dutch phone numbers, e-mail addresses,
 * and postcode + huisnummer combinations. Hard block.
 *
 * Trap 2 — `detectMogelijkeNamen`: heuristic person-name detection with the
 * Domeinwoordenboek allowlist. Soft block: the ask-route returns
 * `naam_bevestiging_nodig` and the user can confirm "geen persoonsnaam" to
 * proceed (`naamBevestigd`), so false positives cost one click, never a
 * dead end.
 *
 * Shared between the Mila ask-route and, later, the Sara→Mila handoff
 * backstop (ADR 0006) — one filter, two enforcement points.
 */
import { isPiiAllowlisted, normalizeTerm } from './domeinwoordenboek.js';
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// NL phone: +31/0031/0 prefix then 9 digits, separators tolerated.
// Requires the prefix so a bare 9-digit BSN doesn't double-report here.
const PHONE_RE = /(?:\+31|0031|(?<![\d-])0)[\s-]?[1-9](?:[\s-]?\d){7,8}(?!\d)/g;
// Candidate BSN: exactly 9 digits, not embedded in a longer digit run and
// not part of a separator-joined digit sequence (1.123456782, 123456782-1);
// ordinary trailing punctuation ("123456782,") must NOT disqualify.
const BSN_CANDIDATE_RE = /(?<!\d)(?<!\d[.,-])\d{9}(?!\d)(?![.,-]\d)/g;
// NL postcode (1234 AB). Flagged only with an adjacent huisnummer — a bare
// postcode can be a legitimate regional question ("wachttijden in 3500").
const POSTCODE_RE = /\b[1-9]\d{3}\s?[A-Za-z]{2}\b/g;
// "1988 in", "2500 mg": a 4-digit number followed by a lowercase function
// word or unit reads like a postcode to the regex but isn't one. Real
// postcodes written in lowercase still pass when the pair isn't ambiguous.
const POSTCODE_LETTER_STOPWORDS = new Set([
    'in',
    'de',
    'en',
    'of',
    'op',
    'om',
    'is',
    'er',
    'te',
    'na',
    'al',
    'we',
    'ze',
    'je',
    'mg',
    'ml',
    'gr',
    'kg',
    'mm',
    'cm',
    'uu',
]);
/** Elfproef: weighted sum (9..2, last digit ×-1) divisible by 11, non-zero. */
export function isValidBsn(digits) {
    if (!/^\d{9}$/.test(digits))
        return false;
    let sum = 0;
    for (let i = 0; i < 8; i++)
        sum += Number(digits[i]) * (9 - i);
    sum -= Number(digits[8]);
    return sum !== 0 && sum % 11 === 0;
}
/**
 * Scan `text` for structured PII. Returns every match (possibly multiple
 * types); empty array = clean. Pure function, no I/O.
 */
export function detectStructuredPii(text) {
    const matches = [];
    for (const m of text.matchAll(EMAIL_RE)) {
        matches.push({ type: 'email', match: m[0] });
    }
    const phoneRanges = [];
    for (const m of text.matchAll(PHONE_RE)) {
        // At least 10 digits total — filters short numeric runs that happen to
        // start with 0 (e.g. "0,5 mg" survives via the [1-9] second digit rule).
        const digitCount = m[0].replace(/\D/g, '').length;
        if (digitCount < 10)
            continue;
        matches.push({ type: 'telefoonnummer', match: m[0] });
        phoneRanges.push([m.index, m.index + m[0].length]);
    }
    for (const m of text.matchAll(BSN_CANDIDATE_RE)) {
        const inPhone = phoneRanges.some(([s, e]) => m.index >= s && m.index < e);
        if (inPhone)
            continue;
        // Elfproef keeps false positives near zero: a random 9-digit number
        // (dosage codes, DSM/ICD-achtige nummers) passes only 1 in 11 times.
        if (isValidBsn(m[0])) {
            matches.push({ type: 'bsn', match: m[0] });
        }
    }
    for (const m of text.matchAll(POSTCODE_RE)) {
        const letters = m[0].slice(-2);
        if (letters === letters.toLowerCase() && POSTCODE_LETTER_STOPWORDS.has(letters)) {
            continue;
        }
        const before = text.slice(Math.max(0, m.index - 12), m.index);
        const after = text.slice(m.index + m[0].length, m.index + m[0].length + 12);
        // Huisnummer: standalone 1-3 digit number (optional letter suffix)
        // directly around the postcode ("Hoofdstraat 12, 3512 AB" / "3512 AB 12").
        const huisnummer = /(?:^|[\s,])[1-9]\d{0,2}[a-zA-Z]?(?:[\s,.]|$)/;
        if (huisnummer.test(before) || huisnummer.test(after)) {
            matches.push({ type: 'postcode_huisnummer', match: m[0] });
        }
    }
    return matches;
}
// ─── Trap 2: mogelijke persoonsnamen ──────────────────────────────────────
// Aanhef-woorden vóór een naam ("meneer Jansen", "patiënt Pietersen").
// Genormaliseerd (lowercase, diacritics gestript), dus "patiënt" staat er
// als "patient". "dr"/"drs" vangen ook collega-verwijzingen ("dr. Terluin").
const AANHEF_WOORDEN = new Set([
    'meneer',
    'mevrouw',
    'mevr',
    'mw',
    'dhr',
    'heer',
    'dokter',
    'dr',
    'drs',
    'huisarts',
    'patient',
    'patiente',
    'client',
    'cliente',
    'collega',
    'familie',
    'fam',
]);
// Tussenvoegsels die binnen een naam mogen vallen ("van der Berg",
// "Meesters in de Psychologie"). Lowercase in lopende tekst.
const TUSSENVOEGSELS = new Set([
    'van',
    'de',
    'der',
    'den',
    'ten',
    'ter',
    'te',
    'het',
    't',
    'op',
    'aan',
    'in',
]);
// Plaatsbepalende voorzetsels: het enige geval waarin een aanhef gevolgd mag
// worden door een geografische naam ("de familie in Marokko"). Achter een
// naamvoegsel ("mevrouw van Zeeland", "de heer van Utrecht") is een
// plaatsnaam juist meestal een achternaam, dus daar telt geografie niet mee.
const LOCATIEVE_VOORZETSELS = new Set(['in', 'op', 'aan']);
/**
 * De Nederlandse gemeenten en woonplaatsen: apart benoemd omdat ze twee
 * rollen hebben. Ze horen bij de geografie hieronder, maar ze zijn tegelijk
 * de risicogroep voor naamdetectie, want Nederlandse achternamen zijn
 * grotendeels toponiemen. Alleen deze set zet de vorm "in/op/aan + lidwoord
 * + plaatsnaam" weg als achternaam (zie detectMogelijkeNamen); streek- en
 * landnamen doen daar niet aan mee.
 */
const NEDERLANDSE_PLAATSNAMEN = [
    // Bron: de 342 Nederlandse gemeenten (CBS-indeling via Wikipedia) aangevuld
    // met alle woonplaatsen boven 15.000 inwoners (GeoNames), zodat plaatsen die
    // geen gemeentenaam (meer) zijn er ook in staan: Hoofddorp, Drachten,
    // Emmeloord, Veghel, Geleen, Spijkenisse. Empirisch gemeten: 383 van die 401
    // namen gaven een vals naamalarm op "De cliënt in <plaats> is aangemeld".
    //
    // Waarom dit géén echte naamdetectie sloopt, terwijl Nederlandse achternamen
    // grotendeels toponiemen zijn (Familienamenbank, GBA 2007: Heemskerk 5.030
    // naamdragers, Hoogeveen 4.770, Steenbergen 4.675, Kampen 822, Emmen 795,
    // Assen 639, Bergen 560): deze set telt maar op twee plekken mee, en langs
    // geen van beide loopt een achternaamvorm.
    //   1. Na een aanhef alleen achter een KLEINGESCHREVEN in/op/aan
    //      (LOCATIEVE_VOORZETSELS). "de heer Bergen", "mevrouw van Assen" en
    //      "dhr. Holland" hebben dat voorzetsel niet en blijven vuren. Bestaat de
    //      achternaam juist wél uit zo'n voorzetsel, dan valt hij in een van deze
    //      twee vormen, en allebei vuren ze: hoofdletterig ("mevrouw In den
    //      Bosch", "de heer Op den Brink") telt het voorzetsel mee als naamwoord
    //      dat niet op de allowlist staat; kleingeschreven ("de heer op de Beek",
    //      ~2700 naamdragers) staat er een lidwoord tussen, en een gemeentenaam
    //      draagt in het Nederlands nooit een lidwoord. Zie de lidwoord-poort in
    //      detectMogelijkeNamen.
    //   2. Bij patroon 2 moet ELK naamwoord in de frase op de allowlist staan.
    //      "Pieter Bergen" en "Jan van Heemskerk" bevatten een woord dat er niet
    //      op staat en vuren gewoon. Alleen twee plaatsnamen op rij vallen stil,
    //      en dat is geen Nederlandse persoonsnaam.
    //
    // Bewust NIET opgenomen, getoetst aan de Voornamenbank (Meertens, BRP 2018,
    // grens 100 dragers als eerste naam): Stein (856), Veere (183) en Lisse
    // (120). Juist bij die drie is patroon 2 te breken, want een voornaam-plaats
    // plus een achternaam-plaats ("Stein Heemskerk") bestaat uit twee woorden die
    // dan allebei op de allowlist staan. Ede (81) blijft er wel in: onder de
    // grens, en met 125.000 inwoners is "cliënt in Ede" te gewoon om te laten
    // vuren. Verder weggelaten: Holland (geen gemeente of woonplaats, wel 693
    // naamdragers, en Noord-/Zuid-Holland staan al hierboven), Muiden (3.000
    // inwoners, ver onder de ondergrens) en gemeenten met "en" of "bij" in de
    // naam (Berg en Dal, Kaag en Braassem): die woorden zijn geen tussenvoegsel,
    // dus de naamreeks breekt er af en het lemma zou nooit matchen.
    'Aalsmeer',
    'Aalten',
    'Achtkarspelen',
    'Alblasserdam',
    'Albrandswaard',
    'Alkmaar',
    'Almelo',
    'Almere',
    'Alphen-Chaam',
    'Altena',
    'Ameland',
    'Amersfoort',
    'Amstelveen',
    'Amsterdam',
    'Apeldoorn',
    'Arnhem',
    'Assen',
    'Asten',
    'Baarle-Nassau',
    'Baarn',
    'Barendrecht',
    'Barneveld',
    'Beek',
    'Beekdaelen',
    'Beesel',
    'Bennekom',
    'Bergeijk',
    'Bergen',
    'Berkelland',
    'Bernheze',
    'Best',
    'Beuningen',
    'Beverwijk',
    'Bilthoven',
    'Bladel',
    'Blaricum',
    'Blerick',
    'Bloemendaal',
    'Bodegraven',
    'Bodegraven-Reeuwijk',
    'Boekel',
    'Borger-Odoorn',
    'Borne',
    'Borsele',
    'Boskoop',
    'Boxtel',
    'Breda',
    'Bronckhorst',
    'Brummen',
    'Brunssum',
    'Bunnik',
    'Bunschoten',
    'Buren',
    'Bussum',
    'Castricum',
    'Coevorden',
    'Culemborg',
    'Dalfsen',
    'Dantumadeel',
    'De Bilt',
    'De Friese Meren',
    'De Wolden',
    'Delft',
    'Delfzijl',
    'Deurne',
    'Deventer',
    'Didam',
    'Diemen',
    'Dinkelland',
    'Doesburg',
    'Doetinchem',
    'Dongen',
    'Dordrecht',
    'Drachten',
    'Drechterland',
    'Driebergen-Rijsenburg',
    'Drimmelen',
    'Dronten',
    'Druten',
    'Duiven',
    'Echt-Susteren',
    'Edam-Volendam',
    'Ede',
    'Eemnes',
    'Eemsdelta',
    'Eersel',
    'Eibergen',
    'Eijsden-Margraten',
    'Eindhoven',
    'Elburg',
    'Elst',
    'Emmeloord',
    'Emmen',
    'Enkhuizen',
    'Enschede',
    'Epe',
    'Ermelo',
    'Etten-Leur',
    'Geertruidenberg',
    'Geldermalsen',
    'Geldrop',
    'Geldrop-Mierlo',
    'Geleen',
    'Gemert',
    'Gemert-Bakel',
    'Gennep',
    'Goeree-Overflakkee',
    'Goes',
    'Goirle',
    'Gooise Meren',
    'Gorinchem',
    'Gouda',
    'Groesbeek',
    'Gulpen-Wittem',
    'Haaksbergen',
    'Haarlem',
    'Halderberge',
    'Hardenberg',
    'Harderwijk',
    'Hardinxveld-Giessendam',
    'Harlingen',
    'Hattem',
    'Heemskerk',
    'Heemstede',
    'Heerde',
    'Heerenveen',
    'Heerhugowaard',
    'Heerlen',
    'Heeze-Leende',
    'Heiloo',
    'Hellendoorn',
    'Hellevoetsluis',
    'Helmond',
    'Hengelo',
    'Het Hogeland',
    'Heumen',
    'Heusden',
    'Hillegom',
    'Hilvarenbeek',
    'Hilversum',
    'Hoensbroek',
    'Hof van Twente',
    'Hollands Kroon',
    'Hoofddorp',
    'Hoogeveen',
    'Hoogezand',
    'Hoorn',
    'Horst aan de Maas',
    'Houten',
    'Huissen',
    'Huizen',
    'Hulst',
    'IJmuiden',
    'IJsselstein',
    'Julianadorp',
    'Kampen',
    'Kapelle',
    'Katwijk',
    'Katwijk aan Zee',
    'Kerkrade',
    'Koggenland',
    'Laarbeek',
    'Land van Cuijk',
    'Landgraaf',
    'Landsmeer',
    'Lansingerland',
    'Laren',
    'Leek',
    'Leerdam',
    'Leeuwarden',
    'Leiden',
    'Leiderdorp',
    'Leidschendam-Voorburg',
    'Lelystad',
    'Leudal',
    'Leusden',
    'Lichtenvoorde',
    'Lingewaard',
    'Lochem',
    'Loon op Zand',
    'Lopik',
    'Losser',
    'Maarssen',
    'Maasdriel',
    'Maasgouw',
    'Maashorst',
    'Maassluis',
    'Maastricht',
    'Medemblik',
    'Meerssen',
    'Meierijstad',
    'Meppel',
    'Middelburg',
    'Middelharnis',
    'Midden-Delfland',
    'Midden-Drenthe',
    'Midden-Groningen',
    'Mijdrecht',
    'Moerdijk',
    'Molenlanden',
    'Montferland',
    'Montfoort',
    'Naaldwijk',
    'Naarden',
    'Neder-Betuwe',
    'Nederweert',
    'Nieuwegein',
    'Nieuwkoop',
    'Nijkerk',
    'Nijmegen',
    'Nijverdal',
    'Nissewaard',
    'Noardeast-Fryslân',
    'Noord-Beveland',
    'Noordenveld',
    'Noordwijk',
    'Noordwijk-Binnen',
    'Nuenen',
    'Nunspeet',
    'Oegstgeest',
    'Oirschot',
    'Oisterwijk',
    'Oldambt',
    'Oldebroek',
    'Oldenzaal',
    'Olst-Wijhe',
    'Ommen',
    'Oost Gelre',
    'Oosterhout',
    'Ooststellingwerf',
    'Oostzaan',
    'Opmeer',
    'Opsterland',
    'Oss',
    'Oude IJsselstreek',
    'Ouder-Amstel',
    'Oudewater',
    'Papendrecht',
    'Pekela',
    'Pijnacker',
    'Pijnacker-Nootdorp',
    'Purmerend',
    'Putten',
    'Raalte',
    'Reimerswaal',
    'Renkum',
    'Renswoude',
    'Reusel-De Mierden',
    'Rheden',
    'Rhenen',
    'Ridderkerk',
    'Rijen',
    'Rijssen',
    'Rijssen-Holten',
    'Rijswijk',
    'Roerdalen',
    'Roermond',
    'Roosendaal',
    'Rotterdam',
    'Rozendaal',
    'Rucphen',
    'Sassenheim',
    'Schagen',
    'Scherpenzeel',
    'Schiedam',
    'Schiermonnikoog',
    'Schijndel',
    'Schouwen-Duiveland',
    'Simpelveld',
    'Sittard',
    'Sittard-Geleen',
    'Sliedrecht',
    'Sluis',
    'Smallingerland',
    'Sneek',
    'Soest',
    'Someren',
    'Spijkenisse',
    'Stadskanaal',
    'Staphorst',
    'Stede Broec',
    'Steenbergen',
    'Steenwijk',
    'Steenwijkerland',
    'Stichtse Vecht',
    'Súdwest-Fryslân',
    'Tegelen',
    'Terneuzen',
    'Terschelling',
    'Texel',
    'Teylingen',
    'Tholen',
    'Tiel',
    'Tietjerksteradeel',
    'Tilburg',
    'Tubbergen',
    'Twenterand',
    'Tynaarlo',
    'Uden',
    'Uitgeest',
    'Uithoorn',
    'Urk',
    'Utrechtse Heuvelrug',
    'Vaals',
    'Valkenburg aan de Geul',
    'Valkenswaard',
    'Veendam',
    'Veenendaal',
    'Veghel',
    'Veldhoven',
    'Velp',
    'Velsen',
    'Venlo',
    'Venray',
    'Vianen',
    'Vijfheerenlanden',
    'Vlaardingen',
    'Vlieland',
    'Vlissingen',
    'Voerendaal',
    'Volendam',
    'Voorburg',
    'Voorne aan Zee',
    'Voorschoten',
    'Voorst',
    'Vught',
    'Waadhoeke',
    'Waalre',
    'Waalwijk',
    'Waddinxveen',
    'Wageningen',
    'Wassenaar',
    'Weert',
    'Weesp',
    'West Betuwe',
    'Westerkwartier',
    'Westerveld',
    'Westervoort',
    'Westerwolde',
    'Weststellingwerf',
    'Wierden',
    'Wijchen',
    'Wijdemeren',
    'Winschoten',
    'Winterswijk',
    'Woensdrecht',
    'Woerden',
    'Wormerland',
    'Woudenberg',
    'Zaandam',
    'Zaanstad',
    'Zaltbommel',
    'Zandvoort',
    'Zeewolde',
    'Zeist',
    'Zevenaar',
    'Zoetermeer',
    'Zoeterwoude',
    'Zuidplas',
    'Zundert',
    'Zutphen',
    'Zwartewaterland',
    'Zwijndrecht',
    'Zwolle',
];
/**
 * Geografie die in Nederland óók een gangbare voornaam is. Deze termen tellen
 * WEL mee als plaatsbepaling na een aanhef ("de cliënt in India is
 * aangemeld"), maar NIET bij patroon 2 midden in de zin.
 *
 * Waarom die splitsing nodig is: patroon 2 onderdrukt een frase zodra ELK
 * naamwoord erin op de geografielijst staat, en Nederlandse achternamen zijn
 * grotendeels toponiemen die daar per definitie op staan. Een geografische
 * term die tegelijk een voornaam is, maakt het paar dus compleet: "India
 * Bergen" bestond uit twee toegestane woorden en viel stil, terwijl het
 * verder identieke "Pieter Bergen" gewoon vuurde.
 *
 * Waarom demoveren en niet schrappen, zoals verderop bij de volksnaamwoorden:
 * een volksnaamwoord komt nooit achter in/op/aan te staan, dus daar kost
 * schrappen geen enkele onderdrukking. Bij een landnaam wel, en na een aanhef
 * is één naamwoord al genoeg voor een treffer. Schrappen zou "de cliënt in
 * India is aangemeld" laten vuren, en migratieachtergrond is juist klinisch
 * dragende context.
 *
 * Getoetst aan de Nederlandse Voornamenbank (Meertens, BRP 2018, grens 100
 * dragers als eerste naam, mannen plus vrouwen, dezelfde telling die Stein op
 * 856 en Ede op 81 zet) over alle 556 enkelvoudige termen in
 * GEOGRAFISCHE_TERMEN die geen Nederlandse gemeente zijn. Dit zijn de enige
 * drie boven de grens; het aantal staat per term erachter. Wat er vlak onder
 * zit en dus blijft staan: Saba (83), Georgië (66), Sahel (63), Mali (44),
 * Israël (41), Jamaica (27), Servië (24), Thais (17), Cuba (15), Chili (10),
 * Kenia (6) en China (5). Angola, Ghana, Congo, Guinee, Libanon, Roemenië,
 * Bosnië, Oman en Nepal komen in de Voornamenbank helemaal niet voor; Syrië,
 * Iran en Irak blijven onder de vijf dragers.
 *
 * Dezelfde 556 termen zijn ook langs de Nederlandse Familienamenbank (CBG,
 * GBA 2007) gehaald: 47 bestaan als familienaam, waarvan zeven boven de 100
 * naamdragers (Westland 1.150, Limburg 950, Franse 762, Drenthe 379,
 * Oostenrijk 251, Gelderland 207, Mali 153). Die zeven blijven staan, om
 * dezelfde reden als de gemeentenamen hierboven: een ACHTERnaam op de lijst
 * breekt patroon 2 niet zolang er geen VOORnaam meer op staat. Het eerste
 * woord van een Nederlandse persoonsnaam valt dan altijd buiten de lijst en
 * de frase vuurt ("Jan Franse", "Peter Mali"). De voornaamtoets over de 344
 * enkelvoudige gemeentenamen gaf nul treffers boven de grens (Stein, Veere en
 * Lisse waren er al uit gelaten), dus die eigenschap geldt na deze ronde voor
 * de hele allowlist. Zet een term hier daarom niet terug in
 * GEOGRAFISCHE_TERMEN zonder eerst de Voornamenbank te raadplegen.
 */
const GEOGRAFIE_ALLEEN_PLAATSBEPALING = [
    'Dominica', // 209 dragers als eerste naam
    'India', // 262
    'Mauritius', // 352
];
/**
 * Geografische namen: landen, nationaliteiten/bevolkingsgroepen, werelddelen,
 * regio's en meerwoordige plaatsnamen.
 *
 * Waarom een aparte set naast de PII_ALLOWLIST van het Domeinwoordenboek:
 *   1. Het Domeinwoordenboek is óók de dictaat-correctielijst van Sara en
 *      wordt geëxporteerd naar Python; honderden landnamen horen daar niet
 *      thuis, ze corrigeren niets.
 *   2. Belangrijker: deze set geldt UITSLUITEND voor de meerwoords-heuristiek
 *      midden in de zin, nooit na een aanhef. Na "mevrouw"/"de heer"/"dhr."
 *      is één naamwoord al genoeg voor een treffer, en juist daar zijn
 *      plaatsnamen vaak achternamen ("mevrouw Van Assen", "de heer Bergen",
 *      "dhr. Holland"). Zou de allowlist daar meetellen, dan verdwijnt echte
 *      naamdetectie.
 *
 * Losse plaatsnamen horen hier WEL thuis. Een eerdere versie van dit
 * commentaar stelde dat "Utrecht" of "Amsterdam" de heuristiek toch niet laat
 * afgaan; dat klopte alleen voor patroon 2, dat twee naamvormige woorden eist.
 * Sinds de uitzondering op LOCATIEVE_VOORZETSELS bestaat, is één naamwoord na
 * een aanhef al genoeg en gaf "de cliënt in Amsterdam" wel degelijk een
 * melding. Dat is precies de vraagvorm die de Sociale Kaart uitlokt, dus de
 * Nederlandse gemeenten en woonplaatsen horen er voluit in. Ze staan in
 * NEDERLANDSE_PLAATSNAMEN hierboven en worden hieronder ingevoegd, want ze
 * hebben een tweede rol: alleen die namen zetten de vorm "in/op/aan +
 * lidwoord + plaatsnaam" weg als achternaam.
 */
const GEOGRAFISCHE_TERMEN = [
    // --- Werelddelen en wereldregio's ---------------------------------------
    'Europa',
    'Azië',
    'Afrika',
    'Amerika',
    'Oceanië',
    'Noord-Afrika',
    'West-Afrika',
    'Oost-Afrika',
    'Centraal-Afrika',
    'Zuidelijk Afrika',
    'Sub-Sahara Afrika',
    'Sub-Saharaans Afrika',
    'Hoorn van Afrika',
    'Noord-Amerika',
    'Zuid-Amerika',
    'Midden-Amerika',
    'Centraal-Amerika',
    'Latijns-Amerika',
    'West-Europa',
    'Oost-Europa',
    'Noord-Europa',
    'Zuid-Europa',
    'Midden-Europa',
    'Centraal-Europa',
    'Zuidoost-Europa',
    'Midden-Oosten',
    'Nabije Oosten',
    'Verre Oosten',
    'Zuidoost-Azië',
    'Zuid-Azië',
    'Oost-Azië',
    'West-Azië',
    'Centraal-Azië',
    'Klein-Azië',
    'Arabisch schiereiland',
    'Arabische wereld',
    'Balkan',
    'Kaukasus',
    'Maghreb',
    'Sahel',
    'Levant',
    'Golfregio',
    'Caraïbisch gebied',
    'Caribisch gebied',
    'Cariben',
    'Caraïben',
    'Antillen',
    'Nederlandse Antillen',
    'Caribisch Nederland',
    'Benedenwindse Eilanden',
    'Bovenwindse Eilanden',
    'Westelijke Jordaanoever',
    'Gazastrook',
    'Baltische Staten',
    'voormalig Joegoslavië',
    'Sovjet-Unie',
    'Sovjetunie',
    'Nederlands-Indië',
    'Nederlands-Nieuw-Guinea',
    'Oost-Duitsland',
    'West-Duitsland',
    'Molukken',
    // --- Landen -------------------------------------------------------------
    'Afghanistan',
    'Albanië',
    'Algerije',
    'Andorra',
    'Angola',
    'Antigua en Barbuda',
    'Argentinië',
    'Armenië',
    'Aruba',
    'Australië',
    'Azerbeidzjan',
    'Bahrein',
    'Bangladesh',
    'Barbados',
    'Belarus',
    'België',
    'Belize',
    'Benin',
    'Bhutan',
    'Bolivia',
    'Bonaire',
    'Bosnië',
    'Bosnië-Herzegovina',
    'Bosnië en Herzegovina',
    'Botswana',
    'Brazilië',
    'Brunei',
    'Bulgarije',
    'Burkina Faso',
    'Burundi',
    'Cambodja',
    'Canada',
    'Centraal-Afrikaanse Republiek',
    'Chili',
    'China',
    'Colombia',
    'Comoren',
    'Congo',
    'Congo-Brazzaville',
    'Congo-Kinshasa',
    'Costa Rica',
    'Cuba',
    'Curaçao',
    'Cyprus',
    'Denemarken',
    'Djibouti',
    // 'Dominica' staat in GEOGRAFIE_ALLEEN_PLAATSBEPALING (voornaam, 209 dragers).
    'Dominicaanse Republiek',
    'Duitsland',
    'Ecuador',
    'Egypte',
    'El Salvador',
    'Equatoriaal-Guinea',
    'Eritrea',
    'Estland',
    'Eswatini',
    'Ethiopië',
    'Fiji',
    'Filipijnen',
    'Finland',
    'Frankrijk',
    'Gabon',
    'Gambia',
    'Georgië',
    'Ghana',
    'Grenada',
    'Griekenland',
    'Guatemala',
    'Guinee',
    'Guinee-Bissau',
    'Guyana',
    'Haïti',
    'Honduras',
    'Hongarije',
    'Ierland',
    'IJsland',
    // 'India' staat in GEOGRAFIE_ALLEEN_PLAATSBEPALING (voornaam, 262 dragers).
    'Indonesië',
    'Irak',
    'Iran',
    'Israël',
    'Italië',
    'Ivoorkust',
    'Jamaica',
    'Japan',
    'Jemen',
    'Joegoslavië',
    'Jordanië',
    'Kaapverdië',
    'Kameroen',
    'Kazachstan',
    'Kenia',
    'Kirgizië',
    'Koeweit',
    'Kosovo',
    'Kroatië',
    'Laos',
    'Lesotho',
    'Letland',
    'Libanon',
    'Liberia',
    'Libië',
    'Liechtenstein',
    'Litouwen',
    'Luxemburg',
    'Madagaskar',
    'Malawi',
    'Maldiven',
    'Maleisië',
    'Mali',
    'Malta',
    'Marokko',
    'Mauritanië',
    // 'Mauritius' staat in GEOGRAFIE_ALLEEN_PLAATSBEPALING (voornaam, 352 dragers).
    'Mexico',
    'Moldavië',
    'Monaco',
    'Mongolië',
    'Montenegro',
    'Mozambique',
    'Myanmar',
    'Namibië',
    'Nederland',
    'Nepal',
    'Nicaragua',
    'Nieuw-Zeeland',
    'Niger',
    'Nigeria',
    'Noord-Korea',
    'Noord-Macedonië',
    'Noord-Ierland',
    'Noorwegen',
    'Oeganda',
    'Oekraïne',
    'Oezbekistan',
    'Oman',
    'Oostenrijk',
    'Pakistan',
    'Palestina',
    'Palestijnse Gebieden',
    'Panama',
    'Papoea-Nieuw-Guinea',
    'Paraguay',
    'Peru',
    'Polen',
    'Portugal',
    'Qatar',
    'Roemenië',
    'Rusland',
    'Rwanda',
    'Saba',
    'Saoedi-Arabië',
    'Saudi-Arabië',
    'Saudi Arabië',
    'Senegal',
    'Servië',
    'Seychellen',
    'Sierra Leone',
    'Singapore',
    'Sint Eustatius',
    'Sint Maarten',
    'Slovenië',
    'Slowakije',
    'Soedan',
    'Somalië',
    'Somaliland',
    'Spanje',
    'Sri Lanka',
    'Suriname',
    'Syrië',
    'Tadzjikistan',
    'Taiwan',
    'Tanzania',
    'Thailand',
    'Togo',
    'Trinidad en Tobago',
    'Tsjaad',
    'Tsjechië',
    'Tunesië',
    'Turkije',
    'Turkmenistan',
    'Uruguay',
    'Vaticaanstad',
    'Venezuela',
    'Verenigd Koninkrijk',
    'Verenigde Arabische Emiraten',
    'Verenigde Staten',
    'Vietnam',
    'Wit-Rusland',
    'Zambia',
    'Zimbabwe',
    'Zuid-Afrika',
    'Zuid-Korea',
    'Zuid-Soedan',
    'Zweden',
    'Zwitserland',
    // --- Nationaliteiten en bevolkingsgroepen -------------------------------
    // Bijvoeglijke én zelfstandige vormen: de heuristiek slaat aan op paren als
    // "Marokkaanse Nederlander" en op koppelingen als "Nederlands-Marokkaanse".
    //
    // Bewust NIET in deze lijst: enkelvoudige volksnaamwoorden die in Nederland
    // ook een gangbare voor- of achternaam zijn. Ze stonden hier wel, en dat
    // verzwakte het filter: patroon 2 laat een frase door zodra ELK naamwoord op
    // de allowlist staat, dus "Frans Duitser" en "Frans Spanjaard" glipten
    // ongezien langs de naamdetectie. Los kan zo'n term de heuristiek toch nooit
    // laten afgaan (patroon 2 eist minstens twee naamvormige woorden), dus
    // schrappen kost geen enkele onderdrukking; ze telden alleen mee in
    // combinaties, en twee volksnaamwoorden op rij is eerder een persoonsnaam
    // dan een geografische aanduiding.
    //
    // Geschrapt na toetsing aan de Nederlandse Familienamenbank (CBG, GBA 2007)
    // en de Nederlandse Voornamenbank (Meertens), grens 100 naamdragers:
    // Fransen 4.762, Engels 3.986, Spaans 3.322, Pool 2.621, Duits 1.185,
    // Turk 1.085, Spanjaard 757, Engelsman 612, Rus 475, Westers 451,
    // Fransman 303, Berbers 255, Griek 197, Oosters 106 als achternaam;
    // Frans 14.241, Berber 1.269, Brit 153, Roma 135 als voornaam.
    // Duitser, Koerd en Nederlander komen in geen van beide banken voor en
    // blijven daarom staan: "Iraakse Koerd" en "Marokkaanse Nederlander" moeten
    // stil blijven. De klinische onderdrukking loopt sowieso via de -e/-se
    // vormen (Franse, Engelse, Duitse, Turkse, Berberse) en de landnamen; die
    // blijven allemaal staan. Zet een geschrapte term dus niet terug zonder
    // eerst de naambanken te raadplegen.
    'Afghaan',
    'Afghaanse',
    'Afghanen',
    'Afghaans',
    'Afrikaans',
    'Afrikaanse',
    'Albanees',
    'Albanese',
    'Albanezen',
    'Algerijn',
    'Algerijns',
    'Algerijnse',
    'Algerijnen',
    'Amerikaan',
    'Amerikaans',
    'Amerikaanse',
    'Amerikanen',
    'Antilliaan',
    'Antilliaans',
    'Antilliaanse',
    'Antillianen',
    'Arabier',
    'Arabieren',
    'Arabisch',
    'Arabische',
    'Armeens',
    'Armeense',
    'Armeniër',
    'Armeniërs',
    'Arubaans',
    'Arubaanse',
    'Assyrisch',
    'Assyrische',
    'Aziaat',
    'Aziaten',
    'Aziatisch',
    'Aziatische',
    'Belg',
    'Belgen',
    'Belgisch',
    'Belgische',
    'Bengalees',
    'Bengaalse',
    'Berberse',
    'Bosnisch',
    'Bosnische',
    'Bosniër',
    'Bosniërs',
    'Braziliaan',
    'Braziliaans',
    'Braziliaanse',
    'Brazilianen',
    'Brits',
    'Britse',
    'Britten',
    'Bulgaar',
    'Bulgaars',
    'Bulgaarse',
    'Bulgaren',
    'Canadees',
    'Canadese',
    'Caraïbisch',
    'Caraïbische',
    'Caribisch',
    'Caribische',
    'Chinees',
    'Chinese',
    'Chinezen',
    'Colombiaans',
    'Colombiaanse',
    'Creools',
    'Creoolse',
    'Cubaan',
    'Cubaans',
    'Cubaanse',
    'Cubanen',
    'Curaçaos',
    'Curaçaose',
    'Dominicaan',
    'Dominicaans',
    'Dominicaanse',
    'Dominicanen',
    'Duitse',
    'Duitser',
    'Duitsers',
    'Egyptenaar',
    'Egyptenaren',
    'Egyptisch',
    'Egyptische',
    'Engelse',
    'Eritrees',
    'Eritrese',
    'Eritreeër',
    'Eritreeërs',
    'Ethiopiër',
    'Ethiopiërs',
    'Ethiopisch',
    'Ethiopische',
    'Europeaan',
    'Europeanen',
    'Europees',
    'Europese',
    'Filipijns',
    'Filipijnse',
    'Franse',
    'Ghanees',
    'Ghanese',
    'Ghanezen',
    'Grieks',
    'Griekse',
    'Grieken',
    'Haïtiaans',
    'Haïtiaanse',
    'Hindoestaans',
    'Hindoestaanse',
    'Iers',
    'Ierse',
    'Indiaas',
    'Indiase',
    'Indiër',
    'Indiërs',
    'Indisch',
    'Indische',
    'Indonesiër',
    'Indonesiërs',
    'Indonesisch',
    'Indonesische',
    'Iraakse',
    'Iraaks',
    'Irakees',
    'Irakezen',
    'Iraans',
    'Iraanse',
    'Iraniër',
    'Iraniërs',
    'Israëlisch',
    'Israëlische',
    'Israëliër',
    'Israëliërs',
    'Italiaan',
    'Italiaans',
    'Italiaanse',
    'Italianen',
    'Japanner',
    'Japanners',
    'Japans',
    'Japanse',
    'Javaans',
    'Javaanse',
    'Jemenitisch',
    'Jemenitische',
    'Jezidi',
    'Kaapverdiaan',
    'Kaapverdiaans',
    'Kaapverdiaanse',
    'Kaapverdianen',
    'Kameroens',
    'Kameroense',
    'Koerd',
    'Koerden',
    'Koerdisch',
    'Koerdische',
    'Koreaan',
    'Koreaans',
    'Koreaanse',
    'Koreanen',
    'Kosovaars',
    'Kosovaarse',
    'Kroaat',
    'Kroaten',
    'Kroatisch',
    'Kroatische',
    'Latijns-Amerikaans',
    'Latijns-Amerikaanse',
    'Libanees',
    'Libanese',
    'Libanezen',
    'Macedonisch',
    'Macedonische',
    'Marokkaan',
    'Marokkaans',
    'Marokkaanse',
    'Marokkanen',
    'Mexicaan',
    'Mexicaans',
    'Mexicaanse',
    'Mexicanen',
    'Moluks',
    'Molukse',
    'Molukker',
    'Molukkers',
    'Nederlander',
    'Nederlanders',
    'Nederlands',
    'Nederlandse',
    'Nederlandstalig',
    'Nederlandstalige',
    'Nepalees',
    'Nepalese',
    'Nigeriaan',
    'Nigeriaans',
    'Nigeriaanse',
    'Nigerianen',
    'Oekraïens',
    'Oekraïense',
    'Oekraïner',
    'Oekraïners',
    'Oosterse',
    'Pakistaan',
    'Pakistaans',
    'Pakistaanse',
    'Pakistani',
    'Palestijn',
    'Palestijns',
    'Palestijnse',
    'Palestijnen',
    'Pools',
    'Poolse',
    'Portugees',
    'Portugese',
    'Portugezen',
    'Riffijns',
    'Riffijnse',
    'Roemeen',
    'Roemeens',
    'Roemeense',
    'Roemenen',
    'Russen',
    'Russisch',
    'Russische',
    'Serviër',
    'Serviërs',
    'Servisch',
    'Servische',
    'Sinti',
    'Soedanees',
    'Soedanese',
    'Soedanezen',
    'Somaliër',
    'Somaliërs',
    'Somalisch',
    'Somalische',
    'Spaanse',
    'Spanjaarden',
    'Srilankaans',
    'Srilankaanse',
    'Surinaams',
    'Surinaamse',
    'Surinamer',
    'Surinamers',
    'Syriër',
    'Syriërs',
    'Syrisch',
    'Syrische',
    'Thais',
    'Thaise',
    'Tunesiër',
    'Tunesiërs',
    'Tunesisch',
    'Tunesische',
    'Turken',
    'Turks',
    'Turkse',
    'Venezolaans',
    'Venezolaanse',
    'Vietnamees',
    'Vietnamese',
    'Vietnamezen',
    'Westerse',
    'Zuid-Afrikaans',
    'Zuid-Afrikaanse',
    // Combinaties die door het schrappen hierboven zouden gaan vuren. Als frase
    // zijn ze veilig: niemand heet "Marokkaanse Berber" of "Nederlands-Duits",
    // en de allowlist toetst eerst de hele frase en pas daarna woord voor woord.
    // Zo blijft migratieachtergrond als klinische context stil, terwijl het paar
    // losse volksnaamwoorden ("Frans Spanjaard") gewoon een treffer geeft.
    'Marokkaanse Berber',
    'Marokkaanse Berbers',
    'Koerdische Turk',
    'Nederlands-Duits',
    'Nederlands-Engels',
    'Nederlands-Frans',
    'Nederlands-Spaans',
    // --- Nederlandse provincies, regio's en meerwoordige plaatsnamen --------
    // Empirisch geverifieerd: deze vormen geven dezelfde vals-positieve
    // melding als de landnamen ("wachttijden in Den Haag", "Noord-Holland").
    'Drenthe',
    'Flevoland',
    'Friesland',
    'Fryslân',
    'Gelderland',
    'Groningen',
    'Limburg',
    'Noord-Brabant',
    'Noord-Holland',
    'Overijssel',
    'Utrecht',
    'Zeeland',
    'Zuid-Holland',
    'Achterhoek',
    'Amstelland',
    'Amsterdam-Amstelland',
    'Betuwe',
    'Gooi en Vechtstreek',
    'Groene Hart',
    'Haaglanden',
    'Het Gooi',
    'IJmond',
    'Kennemerland',
    'Kop van Noord-Holland',
    'Midden-Brabant',
    'Midden-Limburg',
    'Midden-Nederland',
    'Noord-Limburg',
    'Noord-Nederland',
    'Noordoost-Brabant',
    'Oost-Brabant',
    'Oost-Groningen',
    'Oost-Nederland',
    'Randstad',
    'Rijnmond',
    'Rivierenland',
    'Twente',
    'Veluwe',
    'Waterland',
    'West-Brabant',
    'West-Nederland',
    'Westland',
    'Zaanstreek',
    // Deze vier zijn óók gemeente, maar staan hier omdat het Nederlandse
    // spraakgebruik er een lidwoord voor zet ("in de Noordoostpolder"). Ze
    // mogen daarom niet in NEDERLANDSE_PLAATSNAMEN vallen, want die set zet
    // juist de vorm "in de <plaatsnaam>" weg als achternaam.
    'Haarlemmermeer',
    'Krimpenerwaard',
    'Noordoostpolder',
    'Overbetuwe',
    'Zeeuws-Vlaanderen',
    'Zuid-Kennemerland',
    'Zuid-Limburg',
    'Zuid-Nederland',
    'Zuidoost-Brabant',
    'Alphen aan den Rijn',
    'Bergen op Zoom',
    'Berkel en Rodenrijs',
    'Capelle aan den IJssel',
    'Den Bosch',
    'Den Haag',
    'Den Helder',
    'Hendrik-Ido-Ambacht',
    'Hoek van Holland',
    'Hoeksche Waard',
    'Krimpen aan den IJssel',
    'Nieuw-Vennep',
    'Oud-Beijerland',
    "'s-Hertogenbosch",
    'Sint-Michielsgestel',
    'Sint-Oedenrode',
    'Wijk bij Duurstede',
    // Stadsdelen: de windrichting is alleen bruikbaar in combinatie met een
    // stadsnaam, en die staan er daarom als geheel bij.
    'Amsterdam Zuidoost',
    'Amsterdam Noord',
    'Amsterdam West',
    'Amsterdam Oost',
    'Amsterdam Zuid',
    'Rotterdam Zuid',
    'Rotterdam Noord',
    'Rotterdam Rijnmond',
    'Utrecht Overvecht',
    'Den Haag Zuidwest',
    ...NEDERLANDSE_PLAATSNAMEN,
    // --- Meerwoordige buitenlandse plaatsnamen ------------------------------
    'New York',
    'Los Angeles',
    'San Francisco',
    'Sao Paulo',
    'Rio de Janeiro',
    'Buenos Aires',
    'Mexico-Stad',
    'Hong Kong',
    'Kuala Lumpur',
    'New Delhi',
    'Tel Aviv',
    'Abu Dhabi',
    'Addis Abeba',
    'Dar es Salaam',
    'Cape Town',
    'Kaapstad',
    'Port-au-Prince',
    'Santo Domingo',
    'San Juan',
    'Ho Chi Minhstad',
    ...GEOGRAFIE_ALLEEN_PLAATSBEPALING,
];
const _GEOGRAFIE = new Set(GEOGRAFISCHE_TERMEN.map(normalizeTerm));
const _PLAATSNAMEN = new Set(NEDERLANDSE_PLAATSNAMEN.map(normalizeTerm));
const _OOK_VOORNAAM = new Set(GEOGRAFIE_ALLEEN_PLAATSBEPALING.map(normalizeTerm));
/** Is `term` een land, nationaliteit, werelddeel of (meerwoordige) plaatsnaam? */
function isGeografisch(term) {
    return _GEOGRAFIE.has(normalizeTerm(term));
}
/** Is `term` geografie die tegelijk een gangbare Nederlandse voornaam is?
 * Zulke termen tellen alleen als plaatsbepaling mee, nooit bij patroon 2. */
function isOokVoornaam(term) {
    return _OOK_VOORNAAM.has(normalizeTerm(term));
}
/** Is `term` een Nederlandse gemeente of woonplaats? Strikter dan
 * `isGeografisch`: streken, landen en nationaliteiten vallen erbuiten. */
function isPlaatsnaam(term) {
    return _PLAATSNAMEN.has(normalizeTerm(term));
}
function naamTokens(text) {
    const out = [];
    // Apostrof mag in/voor een token ('t, D'Angelo); punten en streepjes niet —
    // die zijn juist de grenzen waar de sequentie-logica op stuurt.
    for (const m of text.matchAll(/'?\p{L}[\p{L}']*/gu)) {
        out.push({ raw: m[0], start: m.index, end: m.index + m[0].length });
    }
    return out;
}
function normalizeWord(raw) {
    return raw
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/'/g, '')
        .toLowerCase();
}
/** Naamvormig woord: begint met hoofdletter, rest bevat kleine letters —
 * ALL-CAPS afkortingen (DSM, ADHD, GGZ) vallen er bewust buiten. */
function isNaamwoord(raw) {
    return raw.length >= 2 && /^\p{Lu}/u.test(raw) && raw !== raw.toUpperCase();
}
/** Woorden mogen alleen door whitespace of een enkel koppelteken gescheiden
 * zijn om één naam te vormen ("Jansen-Smit" wel, "Jansen, Smit" niet).
 * Direct na een aanhef mag ook één punt: "dhr. Jansen", "mevr. de Vries". */
function aansluitend(text, prev, next, naAanhef = false) {
    const gap = text.slice(prev.end, next.start);
    return naAanhef ? /^\.?[\s-]*$/.test(gap) : /^[\s-]*$/.test(gap);
}
/** Zinsbegin: alleen relevant voor de meerwoords-heuristiek — een
 * hoofdletter aan het zinsbegin zegt niets over eigennamen. */
function isZinsbegin(text, tokens, i) {
    if (i === 0)
        return true;
    const gap = text.slice(tokens[i - 1].end, tokens[i].start);
    return /[.!?:;\n]/.test(gap);
}
function isAllowlisted(phrase, parts, geografie) {
    const toegestaan = (w) => isPiiAllowlisted(w) ||
        (geografie !== 'geen' &&
            isGeografisch(w) &&
            (geografie === 'plaatsbepaling' || !isOokVoornaam(w)));
    if (toegestaan(phrase))
        return true;
    const naamwoorden = parts.filter(isNaamwoord);
    return naamwoorden.length > 0 && naamwoorden.every(toegestaan);
}
/**
 * Trap 2 (issue #31): heuristische detectie van mogelijke persoonsnamen.
 * Twee patronen, precisie boven dekking:
 *   1. aanhef + naam ("meneer Jansen", "mevrouw van der Berg") — overal;
 *   2. twee of meer naamvormige woorden op rij ("Jan Jansen") — alleen
 *      midden in de zin, want zinsbegin-hoofdletters zijn ruis.
 * Organisatie-/bron-/vragenlijstnamen uit het Domeinwoordenboek
 * (PII_ALLOWLIST) triggeren niets; nieuwe vals-positieven los je op door
 * een entry toe te voegen, niet door de heuristiek op te rekken.
 * Landen, nationaliteiten en plaatsnamen staan in GEOGRAFISCHE_TERMEN
 * hierboven; hoeveel daarvan meetelt hangt af van de zinsvorm, zie
 * GeografieModus. Migratieachtergrond is klinisch relevante context, maar een
 * achternaam na een aanhef blijft PII.
 *
 * Retourneert de verdachte frasen — die gaan alleen terug naar de gebruiker
 * zelf in de foutmelding, NOOIT naar logs.
 *
 * ponytail: namen aan het zinsbegin ("Jansen belde vandaag") glippen erdoor;
 * bewustwording blijft de primaire control, dit is de vangrail.
 */
export function detectMogelijkeNamen(text) {
    const tokens = naamTokens(text);
    const kandidaten = new Set();
    let i = 0;
    while (i < tokens.length) {
        const isAanhef = AANHEF_WOORDEN.has(normalizeWord(tokens[i].raw));
        const startNaam = !isAanhef && isNaamwoord(tokens[i].raw) && !isZinsbegin(text, tokens, i);
        if (!isAanhef && !startNaam) {
            i++;
            continue;
        }
        // Verzamel de naamreeks: naamwoorden met tussenvoegsels ertussen.
        // Bij aanhef begint de reeks NA het aanhef-woord en telt één naamwoord
        // al als naam; midden in de zin zijn er minstens twee nodig.
        const seq = startNaam ? [tokens[i]] : [];
        let naamwoordCount = startNaam ? 1 : 0;
        let j = i + 1;
        while (j < tokens.length &&
            aansluitend(text, tokens[j - 1], tokens[j], isAanhef && j === i + 1)) {
            const raw = tokens[j].raw;
            if (isNaamwoord(raw)) {
                seq.push(tokens[j]);
                naamwoordCount++;
                j++;
                continue;
            }
            if (TUSSENVOEGSELS.has(normalizeWord(raw))) {
                seq.push(tokens[j]);
                j++;
                continue;
            }
            break;
        }
        // Losse tussenvoegsels aan het eind horen bij de zin, niet bij de naam
        // ("mevrouw van de zorggroep" → geen naam over).
        while (seq.length > 0 && !isNaamwoord(seq[seq.length - 1].raw))
            seq.pop();
        const genoeg = isAanhef ? naamwoordCount >= 1 : naamwoordCount >= 2;
        if (genoeg && seq.length > 0) {
            // Uit de brontekst geknipt zodat "Jansen-Smit" zijn koppelteken houdt.
            const phrase = text.slice(seq[0].start, seq[seq.length - 1].end);
            const raws = seq.map((t) => t.raw);
            // Voor de allowlist-vergelijking tellen alleen de naamwoorden mee, zodat
            // "in het Verenigd Koninkrijk" op "Verenigd Koninkrijk" matcht. De aan de
            // gebruiker getoonde frase blijft ongewijzigd.
            const kern = seq.slice(seq.findIndex((t) => isNaamwoord(t.raw)));
            const kernPhrase = text.slice(kern[0].start, kern[kern.length - 1].end);
            // Na een aanhef telt de geografie-set alleen mee achter een
            // plaatsbepalend voorzetsel, en dan valt één vorm er alsnog buiten:
            // voorzetsel + lidwoord + losse gemeentenaam. Dat is namelijk geen
            // plaatsbepaling maar de klassieke Nederlandse achternaam ("de heer op de
            // Beek", ~2700 naamdragers; "in de Weert", "op den Hoorn"). De taalregel
            // eronder: een gemeentenaam draagt nooit een lidwoord ("in Amsterdam",
            // nooit "in de Amsterdam"), terwijl streek- en meervoudige landnamen dat
            // juist altijd doen ("op de Veluwe", "in de Verenigde Staten"). Die staan
            // niet in NEDERLANDSE_PLAATSNAMEN en blijven dus stil. De eis dat de kern
            // uit één woord bestaat spaart meerwoordige geografie achter een lidwoord
            // ("op de Utrechtse Heuvelrug"); een achternaam van die vorm bestaat niet.
            const directNaVoorzetsel = seq.length >= 2 && seq[1] === kern[0];
            const isPlaatsbepaling = LOCATIEVE_VOORZETSELS.has(normalizeWord(seq[0].raw)) &&
                (directNaVoorzetsel || kern.length > 1 || !isPlaatsnaam(kernPhrase));
            const geografie = !isAanhef
                ? 'los'
                : isPlaatsbepaling
                    ? 'plaatsbepaling'
                    : 'geen';
            if (!isAllowlisted(kernPhrase, raws, geografie)) {
                kandidaten.add(phrase);
            }
        }
        i = j > i ? j : i + 1;
    }
    return [...kandidaten];
}
//# sourceMappingURL=pii.js.map